import { Modal } from 'antd';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAppSelector } from '../store';
import eventBus, { ESocketEvent } from '../socket/eventBus';
import { IPublicWSData, IPublicWSResponse } from '../interfaces/pair';

const URL = process.env.REACT_APP_SERVICE_SOCKET ?? '';

interface SocketContextProps {}

const PublicSocketContext = createContext<SocketContextProps | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

const reconnectInterval = 3000;
const maxReconnectAttempts = 10;

export const PublicSocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const currentPair = useAppSelector((state) => state.pairs.currentPair);
  const selectedSize = useAppSelector((state) => state.pairs.selectedSize);
  const [isSocketInitialized, setIsSocketInitialized] = useState(false);

  const reconnectAttemptsRef = useRef(0);
  const isReconnectingRef = useRef(false); // Prevent multiple calls

  const webSocketRef = useRef<WebSocket | null>(null);

  const attemptReconnect = useCallback(() => {
    if (isReconnectingRef.current) {
      console.log('Reconnect already in progress. Skipping...');
      return;
    }
    isReconnectingRef.current = true;

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('Max reconnect attempts reached. Please refresh the page to try again.');
      isReconnectingRef.current = false;
      window.location.reload(); // Reload the page when the reconnect limit is exceeded.
      return;
    }

    reconnectAttemptsRef.current += 1;
    console.log(`Reconnecting attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts}...`);

    setTimeout(() => {
      isReconnectingRef.current = false;
      webSocketRef.current = null;
      initWebSocket();
    }, reconnectInterval);
  }, [maxReconnectAttempts, reconnectInterval]);

  const initWebSocket = () => {
    if (webSocketRef.current) return;
    console.log('URL', URL);
    const newWs = new WebSocket(`${URL}/api-gateway/v1/public/ws`);
    webSocketRef.current = newWs;

    newWs.onopen = () => {
      reconnectAttemptsRef.current = 0;
      setIsSocketInitialized(true);
    };

    newWs.onmessage = (event) => {
      const data: IPublicWSResponse<IPublicWSData> = JSON.parse(event.data);

      if (data?.topic?.type === 'market_data' && data.success) {
        eventBus.dispatch(ESocketEvent.MARKET_DATA, data);
      }

      if (data?.topic?.type === 'orderbook' && data.success) {
        const data: IPublicWSResponse<IPublicWSData> = JSON.parse(event.data);

        eventBus.dispatch(ESocketEvent.ORDER_BOOK, data);
      }
    };

    newWs.onclose = () => {
      if (webSocketRef.current) {
        console.log('ws closed by server');
      } else {
        console.log('ws closed by app component unmount');
        return;
      }
      if (isReconnectingRef.current) {
        console.log('Reconnection in progress, ignoring close handler.');
        return;
      }
      console.log('WebSocket connection closed. Attempting to reconnect...');
      setIsSocketInitialized(false);
      attemptReconnect();
    };

    newWs.onerror = (error) => {
      console.error('WebSocket error:', error);
      newWs.close();
    };

    return () => {
      webSocketRef.current = null;
      newWs.close();
    };
  };

  useEffect(() => {
    initWebSocket();
  }, []);

  useEffect(() => {
    if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
      const orderBookRequest = {
        topic: {
          type: 'orderbook',
          asset: currentPair.baseToken,
          extra: {
            tickSize: selectedSize ?? currentPair.config?.defaultTickSize,
          },
        },
        action: 'subscribe',
      };
      const marketDataRequest = {
        topic: {
          type: 'market_data',
          asset: currentPair.baseToken,
        },
        action: 'subscribe',
      };
      webSocketRef.current.send(JSON.stringify(orderBookRequest));
      webSocketRef.current.send(JSON.stringify(marketDataRequest));
    }

    return () => {
      if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
        const orderBookRequest = {
          topic: {
            type: 'orderbook',
            asset: currentPair.baseToken,
            extra: {
              tickSize: selectedSize ?? currentPair.config?.defaultTickSize,
            },
          },
          action: 'unsubscribe',
        };
        const marketDataRequest = {
          topic: {
            type: 'market_data',
            asset: currentPair.baseToken,
          },
          action: 'unsubscribe',
        };
        webSocketRef.current.send(JSON.stringify(orderBookRequest));
        webSocketRef.current.send(JSON.stringify(marketDataRequest));
      }
    };
  }, [currentPair, selectedSize, isSocketInitialized]);

  return <PublicSocketContext.Provider value={{}}>{children}</PublicSocketContext.Provider>;
};

export const usePublicSocket = (): SocketContextProps => {
  const context = useContext(PublicSocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
